import React from 'react';

/**
 * Helper function to parse inline markdown (bold/italics) into React elements.
 */
function parseInlineMarkdown(text) {
  if (!text || typeof text !== 'string') return text;
  const parts = text.split(/(\*\*.*?\*\*|\*.*?\*)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={idx}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={idx}>{part.slice(1, -1)}</em>;
    }
    return part;
  });
}

/**
 * Tabelas de horários e datas de uma seção — vêm prontas do backend (montadas a partir da
 * ficha), então aqui é só desenho.
 */
function renderTabelas(tabelas, secIdx) {
  return (tabelas || []).filter(t => (t.linhas || []).length > 0).map((t, tIdx) => (
    <div key={`sec-${secIdx}-t-${tIdx}`}>
      {t.titulo && <div className="document-paragraph"><strong>{t.titulo}</strong></div>}
      <table className="document-table">
        <thead>
          <tr>{(t.colunas || []).map((c, cIdx) => <th key={cIdx}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {t.linhas.map((linha, lIdx) => (
            <tr key={lIdx}>{linha.map((celula, cIdx) => <td key={cIdx}>{celula}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  ));
}

/**
 * Smart paragraph renderer for legal text formatting.
 * Correctly distinguishes:
 * - Quote blocks (Súmulas, OJs, Jurisprudência, Ementas, Blockquotes)
 * - Subheadings (e.g. 3.1 - DAS HORAS EXTRAS)
 * - Standard narrative paragraphs with inline markdown support.
 */
function renderFormattedParagraph(text, pIdx) {
  if (typeof text !== 'string') {
    return <div key={pIdx} className="document-paragraph">{text}</div>;
  }

  const trimmed = text.trim();
  if (!trimmed) return null;

  // 1. Jurisprudence / Súmulas / Explicit Markdown Blockquotes
  const isBlockquoteMarkdown = trimmed.startsWith('>');
  const isLegalCitation = /^(SÚMULA|OJ|ORIENTAÇÃO JURISPRUDENCIAL|TESE JURÍDICA PREVALECENTE|RECURSO DE REVISTA|AGRAVO DE INSTRUMENTO|RECURSO ORDINÁRIO|EMENTA)/i.test(trimmed);

  if (isBlockquoteMarkdown || isLegalCitation) {
    const cleanQuote = trimmed.replace(/^>\s*/, '').replace(/^[“"']|[”"']$/g, '');
    return (
      <blockquote key={pIdx} className="document-quote-block">
        {parseInlineMarkdown(cleanQuote)}
      </blockquote>
    );
  }

  // 2. Subheadings (e.g., "3.1 - DAS FÉRIAS", "1. DOS FATOS", or "**3.1 - DAS FÉRIAS**")
  const isNumberedSubhead = /^(\d{1,2}\.\d{1,2}|\d{1,2}-|\d{1,2}\.)\s+[A-ZÁÉÍÓÚÂÊÔÃÕÇ]{3,}/.test(trimmed);
  const isBoldSubhead = trimmed.startsWith('**') && trimmed.endsWith('**') && trimmed.length < 120;

  if (isNumberedSubhead || isBoldSubhead) {
    const cleanTitle = trimmed.replace(/\*\*/g, '');
    return (
      <div key={pIdx} className="document-subheading">
        {cleanTitle}
      </div>
    );
  }

  // 3. Standard narrative paragraph
  return (
    <div key={pIdx} className="document-paragraph">
      {parseInlineMarkdown(trimmed)}
    </div>
  );
}

// Requerimentos finais — boilerplate processual do escritório. Espelha o template real
// (backend/scripts/build_template.py); o preview omitia o bloco inteiro e a peça parecia
// terminar sem pedido de citação, provas, procedência nem gratuidade.
const REQUERIMENTOS_FINAIS = [
  'a) A notificação do polo passivo para comparecer à audiência e apresentar defesa, sob pena de revelia e confissão quanto à matéria de fato;',
  'b) A produção de todos os meios de prova em direito admitidos, notadamente documental, testemunhal e pericial, sem exceção;',
  'c) A procedência total dos pedidos elencados no RESUMO DOS PEDIDOS, com a condenação da parte reclamada ao pagamento dos valores devidos, acrescidos de juros e correção monetária na forma da lei;',
  'd) Os benefícios da justiça gratuita, nos termos do art. 790, §§3º e 4º, da CLT;',
  'e) A condenação da parte reclamada ao pagamento dos honorários advocatícios contratuais e sucumbenciais.',
];

const formatBRL = (val) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

const parseValor = (p) =>
  parseFloat(String(p.valor ?? p.valor_estimado ?? 0).replace(/[^\d.-]/g, '')) || 0;

// Mesma regra de backend/app/services/docx_renderer.py:format_valor_pedido — pedido sem lastro
// numérico na ficha sai "a apurar", nunca "R$ 0,00".
const formatValorPedido = (val) =>
  val === 0 ? 'a apurar em liquidação de sentença' : formatBRL(val);

// Linha de dedução (1.21) chega com valor negativo — o que a reclamada já pagou, como o acerto por
// PIX do caso-piloto da doméstica (relatório do Dr. Lucas de 23/09/2026). Sai "(−) R$ 1.191,38",
// e não "-R$ 1.191,38", que no quadro se leria como pedido de valor negativo.
const formatValorLinha = (p) => {
  const val = parseValor(p);
  return val < 0 ? `(−) ${formatBRL(-val)}` : formatValorPedido(val);
};

// TOTAL e valor da causa: o pedido subsidiário (art. 326 do CPC) só é apreciado se o principal
// cair, então aparece no quadro com valor mas fica fora da soma; a dedução negativa entra e
// desconta. Linha sem essas marcas soma como sempre somou. Arredonda ao centavo: soma com centavos
// deixa resto de ponto flutuante, e um total zerado pela dedução sairia "R$ 0,00" em vez de "a apurar".
const totalDoQuadro = (pedidos) => Math.round(
  (pedidos || []).reduce((acc, p) => (p?.subsidiario ? acc : acc + parseValor(p)), 0) * 100,
) / 100;

export default function DocumentPreview({ content }) {
  if (!content) return null;

  const {
    comarca = '',
    vara_texto = '',
    claimant_qualification = '',
    reclamados_qualification = [],
    secoes = [],
    pedidos = [],
    local_data = '',
  } = content;

  const totalVal = totalDoQuadro(pedidos);
  const vara = vara_texto ? `${vara_texto} ` : '';

  return (
    <div className="document-preview">
      {/* Header Timbrado */}
      <div className="document-header">
        <div>ÉRICA MENDONÇA CINTRA ADVOCACIA</div>
        OAB/SP 205.440 · OAB/MG 235.982
      </div>

      {/* Endereçamento */}
      <div className="document-salutation">
        EXCELENTÍSSIMO(A) SENHOR(A) DOUTOR(A) JUIZ(A) DO TRABALHO DA {vara}VARA DO TRABALHO DE{' '}
        {comarca ? comarca.toUpperCase() : '___________________'}.
      </div>

      {/* Qualificação do reclamante — termina em "vem, respeitosamente, propor a presente" */}
      {renderFormattedParagraph(claimant_qualification, 'claimant')}

      {/* Título de Reclamação: vem ANTES do "em face de", senão a frase da qualificação
          ("propor a presente" ... "em face de") fica sem objeto. */}
      <div className="document-title">RECLAMAÇÃO TRABALHISTA</div>

      <div className="document-paragraph">em face de,</div>

      {/* Qualificação dos reclamados */}
      {reclamados_qualification && reclamados_qualification.map((rec, idx) => (
        renderFormattedParagraph(rec, `reclamado-${idx}`)
      ))}

      {/* Seções de Mérito — as tabelas de jornada/contrato entram depois do primeiro parágrafo,
          na mesma posição em que o .docx as insere. */}
      {secoes && secoes.map((sec, idx) => (
        <div key={idx}>
          <div className="document-section-title">{sec.titulo}</div>
          {(sec.paragrafos || []).map((p, pIdx) => (
            <React.Fragment key={`sec-${idx}-frag-${pIdx}`}>
              {renderFormattedParagraph(p, `sec-${idx}-p-${pIdx}`)}
              {renderTabelas(
                (sec.tabelas || []).filter(t => (t.apos_paragrafo || 0) === pIdx), idx,
              )}
            </React.Fragment>
          ))}
          {!(sec.paragrafos || []).length && renderTabelas(sec.tabelas, idx)}
          {(sec.itens || []).length > 0 && (
            <ul className="document-list">
              {sec.itens.map((item, iIdx) => <li key={`sec-${idx}-i-${iIdx}`}>{item}</li>)}
            </ul>
          )}
        </div>
      ))}

      {/* Resumo dos Pedidos */}
      <div className="document-section-title">Resumo dos Pedidos</div>
      <div className="document-paragraph">
        Diante do exposto, e para os fins do art. 292 c/c art. 324, §1º, II, do CPC,
        apresenta-se o resumo dos pedidos com os valores estimados:
      </div>

      <table className="document-table">
        <thead>
          <tr>
            <th>Pedido</th>
            <th className="document-table-right">Valor Estimado</th>
          </tr>
        </thead>
        <tbody>
          {pedidos && pedidos.map((p, idx) => (
            <tr key={idx}>
              <td>{p.descricao || p.label}</td>
              <td className="document-table-right">{formatValorLinha(p)}</td>
            </tr>
          ))}
          <tr className="document-table-total">
            <td>TOTAL</td>
            <td className="document-table-right">{formatValorPedido(totalVal)}</td>
          </tr>
        </tbody>
      </table>

      {/* Requerimentos finais */}
      <div className="document-section-title">Dos Requerimentos Finais</div>
      <div className="document-paragraph">Diante de todo o exposto, requer a Vossa Excelência:</div>
      {(content.requerimentos_finais || REQUERIMENTOS_FINAIS).map((item, idx) => (
        <div key={idx} className="document-paragraph">{item}</div>
      ))}

      {/* Mesma redação do template: sem pedido valorado a frase não pode afirmar "R$ 0,00". */}
      <div className="document-paragraph">
        Dá-se à causa o valor {totalVal === 0 ? 'a apurar em liquidação de sentença' : `de ${formatBRL(totalVal)}`}.
      </div>
      <div className="document-paragraph">Termos em que, pede e espera deferimento.</div>

      {/* Rodapé e assinatura */}
      <div className="document-footer">
        <div className="mb-4">{local_data}</div>
        <div className="document-footer-line"></div>
        <div className="document-footer-name">ÉRICA MENDONÇA CINTRA</div>
        <div className="document-footer-oab">OAB/SP 205.440 · OAB/MG 235.982</div>
      </div>
    </div>
  );
}

